import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import Seo, { breadcrumbs } from "../components/Seo";
import { SITE_NAME, absUrl } from "../lib/seo";
import { Skeleton } from "../components/Skeleton";
import { useMinLoading } from "../lib/useMinLoading";
import "../components/styles/Blog.css";

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [firebaseError, setFirebaseError] = useState(null);

  // get blog posts from firestore
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // Dynamic import to catch initialization errors
        const { collection, getDocs, orderBy, query, where } = await import(
          "firebase/firestore"
        );
        const { db } = await import("../firebase");

        // Only live posts, newest first
        const postsCollection = collection(db, "blog");
        const postsQuery = query(
          postsCollection,
          where("status", "==", "live"),
          orderBy("date", "desc")
        );
        const postsSnapshot = await getDocs(postsQuery);

        const postsList = postsSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id, // This will be used as the dynamic key for routing
            ...data,
            // Convert Firebase Timestamp to readable string if it exists
            date:
              data.date && data.date.toDate
                ? data.date.toDate().toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : data.date,
            // Handle any other potential Timestamp fields
            createdAt:
              data.createdAt && data.createdAt.toDate
                ? data.createdAt.toDate().toLocaleDateString()
                : data.createdAt,
            updatedAt:
              data.updatedAt && data.updatedAt.toDate
                ? data.updatedAt.toDate().toLocaleDateString()
                : data.updatedAt,
          };
        });

        setPosts(postsList);
      } catch (error) {
        console.error("❌ Error fetching blog posts:", error);
        setFirebaseError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  const staggerContainer = {
    visible: { transition: { staggerChildren: 0.2 } },
  };

  // Use Firebase posts only
  const displayPosts = posts;

  const SkeletonCard = () => (
    <article className="blog-post-card" aria-hidden="true">
      <div>
        <Skeleton width="60%" height={14} style={{ marginBottom: 20 }} />
        <Skeleton width="90%" height={22} style={{ marginBottom: 10 }} />
        <Skeleton width="65%" height={22} style={{ marginBottom: 24 }} />
        <Skeleton width="100%" height={12} style={{ marginBottom: 10 }} />
        <Skeleton width="95%" height={12} style={{ marginBottom: 10 }} />
        <Skeleton width="70%" height={12} style={{ marginBottom: 28 }} />
      </div>
      <Skeleton width={110} height={14} />
    </article>
  );

  const showSkeleton = useMinLoading(loading);

  if (showSkeleton) {
    return (
      <div className="blog-page">
        {/* Same head as the loaded state minus the post list, so a crawler
            that snapshots mid-fetch still gets a canonical and a title. */}
        <Seo
          title="Blog — bboy creative"
          description="Notes on app development, web solutions, and digital strategy from a San Antonio software studio."
          path="/blog"
        />
        <Navigation />
        <div className="blog-container">
          <div className="blog-header">
            <p>
              Expert insights on app development, web solutions, and digital
              strategy
            </p>
          </div>
          <div className="blog-posts" aria-busy="true" aria-label="Loading blog posts">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page">
      <Seo
        title="Blog — bboy creative"
        description="Notes on app development, web solutions, and digital strategy from a San Antonio software studio."
        path="/blog"
        jsonLd={[
          {
            "@type": "Blog",
            "@id": absUrl("/blog#blog"),
            name: "bboy creative — Blog",
            description:
              "Notes on app development, web solutions, and digital strategy from a San Antonio software studio.",
            url: absUrl("/blog"),
            publisher: { "@type": "Organization", name: SITE_NAME },
            blogPost: posts.map((post) => ({
              "@type": "BlogPosting",
              headline: post.title,
              url: absUrl(`/blog/${post.id}`),
            })),
          },
          breadcrumbs([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
          ]),
        ]}
      />

      <Navigation />

      <motion.div
        className="blog-container"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="blog-header" variants={containerVariants}>
          <p>
            Expert insights on app development, web solutions, and digital
            strategy
          </p>
          {firebaseError && (
            <div
              style={{ color: "#c60000", fontSize: "14px", marginTop: "10px" }}
            >
              Firebase Error: {firebaseError}
            </div>
          )}
          {!loading && posts.length === 0 && !firebaseError && (
            <div style={{ color: "#777", fontSize: "16px", marginTop: "20px" }}>
              No blog posts found. Add some posts to your Firebase "blog"
              collection.
            </div>
          )}
        </motion.div>

        <motion.div className="blog-posts" variants={containerVariants}>
          {displayPosts.map((post) => (
            <motion.article
              key={post.id}
              className="blog-post-card"
              variants={containerVariants}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
            >
              <div className="post-meta">
                <span className="category">{post.category}</span>
                <span className="date">{post.date}</span>
                <span className="read-time">{post.readTime}</span>
              </div>
              <h2>
                {/* Use the document ID for routing */}
                <Link to={`/blog/${post.id}`}>{post.title}</Link>
              </h2>
              <p className="excerpt">{post.excerpt}</p>
              <Link to={`/blog/${post.id}`} className="read-more">
                Read More →
              </Link>
            </motion.article>
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Blog;
